import React, { useEffect } from 'react';

import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { FormattedMessage } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationCircle } from '@fortawesome/free-solid-svg-icons';

import { GIButton, Modal } from 'app-components';
import PinInput from '../../common/form/PinInput';
import { ConditionalRendering } from '@components/common/conditional-rendering';
import Tooltip from '@components/common/tooltip/Tooltip';
import ResendDropdown from './ResendDropdown';
import { useAppDispatch, useAppSelector } from '@hooks/index';
import { environmentSelector } from '@selectors/commonSelectors';

import useSelfDesignationOtp, { OTP_LENGTH } from '../hooks/useSelfDesignationOtp';
import { ChannelType, StatusType } from '../types/selfDesignationOtpTypes';
import { maskPhoneNumber, maskEmail } from '@utils/miscellaneous/mask.utils';
import { NUMBER } from '@constants/common/number';
import useSelfDesignationOtpRole from '../hooks/useSelfDesignationOtpRole';

const StyledBox = styled.div`
    .gi-input {
        text-align: left;
    }
    .gi-modal__body {
        margin-bottom: 40px;
        overflow-y: visible;
    }
`;

const StyledErrorMessageWrapper = styled.div`
    p {
        color: ${({ theme }) => theme.dangerMain} !important;
        font-size: 14px;
        margin-left: 5px;
        margin-top: 0;
    }
`;

const StyledFlexBox = styled.div`
    display: flex;
    align-items: center;

    .shown.error--msg {
        margin: 0;
    }
`;

const ResendContainer = styled.div`
    align-items: center;
    margin-top: 10px;
    margin-bottom: 10px;
`;

function SelfDesignationOtpModal(props) {
    const {
        actions: { hideModal },
        modal: {
            modalData: { becomeAOR, edeSearchResult },
        },
        modalHeaderProps: { modalId },
    } = props;

    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { role } = useSelfDesignationOtpRole();
    const { isState } = useAppSelector(environmentSelector);

    const {
        otp,
        setOtp,
        otpError,
        setOtpError,
        isLoading,
        setIsLoading,
        resendTimerRawTime,
        resendTimerIsRunning,
        expirationTimerRawTime,
        expirationTimerStart,
        resendCount,
        remainingSendCount,
        remainingVerifyCount,
        otpMedium,
        otpMediumValue,
        sendOTPCall,
        resendOTPCall,
        onSubmit,
        showErrorModal,
        onCloseModal,
        resendConfig,
    } = useSelfDesignationOtp({
        dispatch,
        hideModal,
        becomeAOR,
        edeSearchResult,
        navigate,
        role,
    });

    useEffect(() => {
        setIsLoading(true);
        expirationTimerStart();
        sendOTPCall();
    }, []);

    useEffect(() => {
        if (expirationTimerRawTime <= 0) {
            hideModal();
            showErrorModal(StatusType.MAX_TIME);
        }
    }, [expirationTimerRawTime]);

    const formatTimer = (seconds: number, idMinutes: string, idSeconds: string) => {
        const minutes = Math.floor(seconds / 60);
        const secs = seconds % 60;

        return (
            <FormattedMessage
                id={minutes > 0 ? idMinutes : idSeconds}
                values={{
                    minutes: minutes > 0 ? <strong>{minutes.toString().padStart(2, '0')}</strong> : undefined,
                    seconds: <strong>{secs.toString().padStart(2, '0')}</strong>,
                }}
            />
        );
    };

    const formatExpirationCodeTimer = (seconds: number) =>
        formatTimer(
            seconds,
            `selfDesignation.${role}.search.searchResult.modal.otp.expiry.validForMinutes`,
            `selfDesignation.${role}.search.searchResult.modal.otp.expiry.validForSeconds`,
        );

    const formatResendTimer = (seconds: number) =>
        formatTimer(
            seconds,
            `selfDesignation.${role}.search.searchResult.modal.otp.resend.timerMinutes`,
            `selfDesignation.${role}.search.searchResult.modal.otp.resend.timerSeconds`,
        );

    const errorContentMessage = (otpError: boolean) =>
        isState.PA && otpError ? `selfDesignation.${role}.search.searchResult.modal.otp.body.content1.error` : `selfDesignation.${role}.search.searchResult.modal.otp.body.content1`
    ;

    return (
        <ConditionalRendering isTrue={!isLoading}>
            <Modal id="self-designation-otp-modal">
                <Modal.Header
                    id="self-designation-otp-modal-header"
                    titleId={`selfDesignation.${role}.search.searchResult.modal.otp.header`}
                    onHide={onCloseModal}
                />
                <StyledBox>
                    <Modal.Body id="self-designation-otp-modal-body">
                        <p>
                            <FormattedMessage id={errorContentMessage(!!otpError)} />
                        </p>
                        <p>
                            <FormattedMessage
                                id={
                                    otpMedium === ChannelType.EMAIL
                                        ? `selfDesignation.${role}.search.searchResult.modal.otp.body.content2.email`
                                        : `selfDesignation.${role}.search.searchResult.modal.otp.body.content2.phone`
                                }
                                values={{
                                    mediumValue: (
                                        <strong>
                                            {otpMedium === ChannelType.EMAIL
                                                ? maskEmail(otpMediumValue)
                                                : maskPhoneNumber(otpMediumValue)}
                                        </strong>
                                    ),
                                    remainingSendCount,
                                }}
                            />
                        </p>
                        <p>
                            <FormattedMessage id={`selfDesignation.${role}.search.searchResult.modal.otp.enterOtp`} />
                        </p>
                        <StyledFlexBox>
                            <PinInput {...{ otp, setOtp, otpError, setOtpError }} />
                        </StyledFlexBox>
                        <StyledErrorMessageWrapper>
                            <ConditionalRendering isTrue={!!otpError}>
                                <p>
                                    <FontAwesomeIcon icon={faExclamationCircle} />{' '}
                                    <FormattedMessage
                                        id={`selfDesignation.${role}.search.searchResult.modal.otp.invalidOtp`}
                                        values={{ remainingVerifyCount }}
                                    />
                                </p>
                            </ConditionalRendering>
                        </StyledErrorMessageWrapper>
                        <p>{formatExpirationCodeTimer(expirationTimerRawTime)}</p>
                        <ConditionalRendering isTrue={resendCount < NUMBER.TWO}>
                            <ResendContainer>
                                <FormattedMessage id={`selfDesignation.${role}.search.searchResult.modal.otp.resend.notrecieve`} />

                                <Tooltip
                                    {...{
                                        id: (
                                            <ResendDropdown
                                                title='modals.body.resend'
                                                callback={resendOTPCall}
                                                resendConfig={resendConfig}
                                                isDisabled={resendTimerIsRunning}
                                            />
                                        ),
                                        content: `selfDesignation.${role}.search.searchResult.modal.otp.resend.tooltip`,
                                    }}
                                />
                                <ConditionalRendering isTrue={resendTimerIsRunning}>
                                    {formatResendTimer(resendTimerRawTime)}
                                </ConditionalRendering>
                            </ResendContainer>
                        </ConditionalRendering>
                        <ConditionalRendering isTrue={resendCount >= NUMBER.TWO}>
                            <p>
                                <FormattedMessage id={`selfDesignation.${role}.search.searchResult.modal.otp.resend.max`} />
                            </p>
                        </ConditionalRendering>
                    </Modal.Body>
                </StyledBox>
                <Modal.Controls>
                    <GIButton
                        id={`${modalId}-modalCancelButton`}
                        onClickHandler={onCloseModal}
                        variant="secondary"
                        labelId="button.cancel"
                    />
                    <GIButton
                        id={`${modalId}-modalSubmitButton`}
                        labelId="button.submit"
                        variant="primary"
                        isDisabled={otp.join('').length < OTP_LENGTH || !!otpError}
                        onClickHandler={onSubmit}
                    />
                </Modal.Controls>
            </Modal>
        </ConditionalRendering>
    );
}

export default SelfDesignationOtpModal;
